/**
 * ProductManager.jsx — admin UI for the demo Products catalog.
 *
 * A self-contained CRUD screen (add / edit / view / delete) over the
 * admin_service /products API. Exercises mixed field types: text (name, sku),
 * dropdown (category), long text (description), number (price), and two file
 * uploads (image + document). This catalog is what the Flow Builder api_call
 * node fetches so a flow can render the response as a table / images in chat.
 */
import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2, Upload, X, FileText, ExternalLink } from "lucide-react";
import APICall, { baseURL } from "../../APICalls/APICall";
import PageSizeSelect from "../../components/PageSizeSelect";
import "./ProductManager.css";

const EMPTY = {
  id: null, name: "", sku: "", category: "", description: "",
  price: "", currency: "usd", image_url: "", document_url: "", in_stock: true,
};

// Multipart upload — APICall only speaks JSON, so post FormData with the same
// bearer token the rest of the app uses (no Content-Type: the browser sets the
// multipart boundary itself).
async function uploadFile(file, kind) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  const res = await fetch(`${baseURL}/products/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Upload failed");
  }
  return (await res.json()).url;
}

export default function ProductManager() {
  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [search, setSearch]         = useState("");
  const [pageSize, setPageSize]     = useState(10);
  const [page, setPage]             = useState(1);

  // Modal state
  const [showModal, setShowModal]   = useState(false);
  const [form, setForm]             = useState(EMPTY);
  const [saving, setSaving]         = useState(false);
  const [uploading, setUploading]   = useState("");   // "image" | "document" | ""
  const [modalErr, setModalErr]     = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const data = await APICall.getT("/products");
      setProducts(data.products || []);
    } catch (e) {
      setError(e?.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    APICall.getT("/products/categories")
      .then((d) => setCategories(d.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q)
    );
  }, [products, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows   = filtered.slice((page - 1) * pageSize, page * pageSize);

  const openAdd = () => { setForm(EMPTY); setModalErr(""); setShowModal(true); };
  const openEdit = (p) => {
    setForm({
      id: p.id, name: p.name || "", sku: p.sku || "", category: p.category || "",
      description: p.description || "",
      price: p.price_cents != null ? (p.price_cents / 100).toString() : "",
      currency: p.currency || "usd",
      image_url: p.image_url || "", document_url: p.document_url || "",
      in_stock: !!p.in_stock,
    });
    setModalErr(""); setShowModal(true);
  };

  const onUpload = async (file, kind) => {
    if (!file) return;
    setUploading(kind); setModalErr("");
    try {
      const url = await uploadFile(file, kind);
      setForm((f) => ({ ...f, [kind === "image" ? "image_url" : "document_url"]: url }));
    } catch (e) {
      setModalErr(e?.message || "Upload failed");
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    if (!form.name.trim()) { setModalErr("Name is required"); return; }
    setSaving(true); setModalErr("");
    const payload = {
      name: form.name.trim(),
      sku: form.sku.trim() || null,
      category: form.category || null,
      description: form.description || null,
      price_cents: form.price === "" ? null : Math.round(parseFloat(form.price) * 100),
      currency: form.currency || "usd",
      image_url: form.image_url || null,
      document_url: form.document_url || null,
      in_stock: !!form.in_stock,
    };
    try {
      if (form.id) await APICall.postT(`/products/${form.id}`, payload);
      else         await APICall.postT("/products", payload);
      setShowModal(false);
      await load();
    } catch (e) {
      setModalErr(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try { await APICall.postT(`/products/${p.id}/delete`, {}); await load(); }
    catch (e) { setError(e?.message || "Delete failed"); }
  };

  const money = (p) =>
    p.price_cents == null ? "—" : `${(p.currency || "usd").toUpperCase()} ${p.price}`;

  return (
    <div className="page-content p-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h4 className="mb-1">Products <span className="badge bg-secondary">Demo API</span></h4>
          <small className="text-muted">
            Example catalog for the Flow Builder <code>api_call</code> node — add records here,
            then a flow can fetch and render them.
          </small>
        </div>
        <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" onClick={openAdd}>
          <Plus size={15} /> Add Product
        </button>
      </div>

      {/* Search */}
      <div className="mb-2" style={{ maxWidth: 320 }}>
        <input
          className="form-control form-control-sm"
          placeholder="Search name / SKU / category…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {/* Table */}
      <div className="bg-white rounded-4 shadow overflow-auto d-flex flex-column">
        <div className="table-responsive flex-grow-1">
          <table className="table table-hover mb-0 align-middle">
            <thead className="table-light sticky-top">
              <tr>
                <th style={{ width: 60 }}>S.No</th>
                <th style={{ width: 64 }}>Image</th>
                <th>Name</th>
                <th>SKU</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Doc</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-4 text-muted">Loading…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-4 text-muted">No products yet — click “Add Product”.</td></tr>
              ) : (
                pageRows.map((p, i) => (
                  <tr key={p.id}>
                    <td>{(page - 1) * pageSize + i + 1}</td>
                    <td>
                      {p.image_url
                        ? <img src={p.image_url} alt={p.name} style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6 }} />
                        : <span className="text-muted">—</span>}
                    </td>
                    <td className="fw-semibold">{p.name}</td>
                    <td>{p.sku || <span className="text-muted">—</span>}</td>
                    <td>{p.category ? <span className="badge bg-light text-dark">{p.category}</span> : <span className="text-muted">—</span>}</td>
                    <td>{money(p)}</td>
                    <td>{p.in_stock
                      ? <span className="badge bg-success">In stock</span>
                      : <span className="badge bg-secondary">Out</span>}</td>
                    <td>
                      {p.document_url
                        ? <a href={p.document_url} target="_blank" rel="noreferrer" title="Open document"><FileText size={16} /></a>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openEdit(p)} title="Edit"><Pencil size={14} /></button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(p)} title="Delete"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <PageSizeSelect value={pageSize} onChange={(n) => { setPageSize(n); setPage(1); }} />
            <small className="text-muted">Page {page} of {totalPages} · {filtered.length} item(s)</small>
          </div>
          {totalPages > 1 && (
            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
              <button className="btn btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit modal */}
      {showModal && (
        <div className="cvq-modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="cvq-modal card shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h5 className="mb-0">{form.id ? "Edit product" : "Add product"}</h5>
              <button className="btn btn-sm btn-light" onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="p-3" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {modalErr && <div className="alert alert-danger py-2">{modalErr}</div>}

              <div className="mb-2">
                <label className="form-label mb-1">Name <span className="text-danger">*</span></label>
                <input className="form-control" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>

              <div className="row">
                <div className="col-md-6 mb-2">
                  <label className="form-label mb-1">SKU</label>
                  <input className="form-control" value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })} />
                </div>
                <div className="col-md-6 mb-2">
                  <label className="form-label mb-1">Category</label>
                  <select className="form-select" value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option value="">— Select —</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-2">
                  <label className="form-label mb-1">Price ({(form.currency || "usd").toUpperCase()})</label>
                  <input type="number" min="0" step="0.01" className="form-control" value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0.00" />
                </div>
                <div className="col-md-6 mb-2 d-flex align-items-end">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="inStock" checked={form.in_stock}
                      onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} />
                    <label className="form-check-label" htmlFor="inStock">In stock</label>
                  </div>
                </div>
              </div>

              <div className="mb-2">
                <label className="form-label mb-1">Description</label>
                <textarea className="form-control" rows={3} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>

              {/* Image upload */}
              <div className="mb-2">
                <label className="form-label mb-1">Image</label>
                <div className="d-flex align-items-center gap-2">
                  {form.image_url && <img src={form.image_url} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }} />}
                  <label className="btn btn-sm btn-outline-secondary mb-0 d-inline-flex align-items-center gap-1">
                    <Upload size={14} /> {uploading === "image" ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/*" hidden
                      onChange={(e) => onUpload(e.target.files?.[0], "image")} />
                  </label>
                  {form.image_url && <button className="btn btn-sm btn-link text-danger" onClick={() => setForm({ ...form, image_url: "" })}>Remove</button>}
                </div>
              </div>

              {/* Document upload */}
              <div className="mb-2">
                <label className="form-label mb-1">Document</label>
                <div className="d-flex align-items-center gap-2">
                  {form.document_url && (
                    <a href={form.document_url} target="_blank" rel="noreferrer" className="d-inline-flex align-items-center gap-1">
                      <FileText size={16} /> View <ExternalLink size={12} />
                    </a>
                  )}
                  <label className="btn btn-sm btn-outline-secondary mb-0 d-inline-flex align-items-center gap-1">
                    <Upload size={14} /> {uploading === "document" ? "Uploading…" : "Upload document"}
                    <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" hidden
                      onChange={(e) => onUpload(e.target.files?.[0], "document")} />
                  </label>
                  {form.document_url && <button className="btn btn-sm btn-link text-danger" onClick={() => setForm({ ...form, document_url: "" })}>Remove</button>}
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 p-3 border-top">
              <button className="btn btn-light" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !!uploading}>
                {saving ? "Saving…" : form.id ? "Save changes" : "Create product"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
